import { useState } from "react";
import { Box, Container, Typography, Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ContactModal from "../landing/ContactModal";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";

const GOLD      = "#D97706";

const Section = ({ title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", mb: 2, fontFamily: "IBM Plex Sans, sans-serif", letterSpacing: "-.3px" }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const P = ({ children }) => (
  <Typography sx={{ fontSize: 15, color: "#475569", lineHeight: 1.85, mb: 2, fontFamily: "IBM Plex Sans, sans-serif" }}>
    {children}
  </Typography>
);

const Li = ({ children }) => (
  <Box sx={{ display: "flex", gap: 1.5, mb: 1.25 }}>
    <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: GOLD, mt: "10px", flexShrink: 0 }} />
    <Typography sx={{ fontSize: 15, color: "#475569", lineHeight: 1.8, fontFamily: "IBM Plex Sans, sans-serif" }}>{children}</Typography>
  </Box>
);

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <Box sx={{ minHeight: "100vh", background: "#fff", fontFamily: "IBM Plex Sans, sans-serif" }}>
      <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} />

      {/* Header */}
      <Box sx={{ background: "#0F172A", py: 5, borderBottom: "1px solid rgba(255,255,255,.06)" }}>
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
            <IconButton onClick={() => navigate("/")} sx={{ color: "#64748B", "&:hover": { color: "#fff" }, p: .5 }}>
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Box component="img" src="/logo.png" alt="AMLDiligence" sx={{ height: 34, display: "block", filter: "brightness(0) invert(1)" }} />
          </Box>
          <Typography sx={{ fontSize: { xs: 28, md: 38 }, fontWeight: 700, color: "#F1F5F9", letterSpacing: "-1px", fontFamily: "IBM Plex Sans, sans-serif", mb: 1 }}>
            Privacy Policy
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#64748B", fontFamily: "IBM Plex Sans, sans-serif" }}>
            Last updated: June 2025 · Effective date: June 1, 2025
          </Typography>
        </Container>
      </Box>

      {/* Body */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ maxWidth: 760 }}>

          <P>
            AMLDiligence, a product of ZAHADS Chartered Accountants LLC ("we", "us", or "our"), is committed to protecting the privacy and security of your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you use our AML compliance platform and website at amldiligence.com.
          </P>
          <P>
            By accessing or using the Platform, you agree to the collection and use of information in accordance with this policy. If you do not agree with its terms, please do not use the Platform.
          </P>

          <Section title="1. Information We Collect">
            <P>We collect information you provide directly to us and information generated through your use of the Platform:</P>
            <Li><strong>Account information:</strong> Name, business email address, company name, job title, and phone number when you register or request a demo.</Li>
            <Li><strong>KYC & compliance data:</strong> Customer records, identification documents, beneficial ownership information, and risk assessment data that you input into the Platform for your own AML compliance obligations.</Li>
            <Li><strong>Usage data:</strong> Log files, IP addresses, browser type, device identifiers, pages visited, and feature interactions collected automatically.</Li>
            <Li><strong>Communications:</strong> Messages you send us via contact forms, email, or support channels.</Li>
            <Li><strong>Payment information:</strong> Billing details processed securely by our payment processors; we do not store full card numbers.</Li>
          </Section>

          <Section title="2. How We Use Your Information">
            <Li>Provide, operate, and maintain the AMLDiligence platform and its features.</Li>
            <Li>Process account registrations, demo requests, and subscription management.</Li>
            <Li>Send transactional communications (receipts, alerts, password resets).</Li>
            <Li>Send service updates, compliance news, and marketing communications (you may opt out at any time).</Li>
            <Li>Monitor and analyse usage patterns to improve platform performance and user experience.</Li>
            <Li>Detect, prevent, and respond to fraud, security incidents, or misuse.</Li>
            <Li>Comply with applicable legal obligations including UAE AML/CFT regulations.</Li>
          </Section>

          <Section title="3. Legal Basis for Processing (GDPR / UAE PDPL)">
            <P>Where applicable, we process personal data under the following legal bases:</P>
            <Li><strong>Contract performance:</strong> Processing necessary to deliver the services you have subscribed to.</Li>
            <Li><strong>Legitimate interests:</strong> Security monitoring, fraud prevention, and product improvement.</Li>
            <Li><strong>Legal obligation:</strong> Compliance with UAE Federal Decree-Law No. 45 of 2021 on Personal Data Protection (PDPL) and other applicable laws.</Li>
            <Li><strong>Consent:</strong> Marketing communications and optional analytics cookies.</Li>
          </Section>

          <Section title="4. Data Sharing and Disclosure">
            <P>We do not sell your personal data. We may share data with:</P>
            <Li><strong>Service providers:</strong> Hosting, email delivery, payment processing, and analytics vendors acting as data processors under appropriate agreements.</Li>
            <Li><strong>Sanctions & PEP database providers:</strong> To perform the screening services you request; only the data necessary for a specific screening query is transmitted.</Li>
            <Li><strong>Regulatory authorities:</strong> UAE FIU, CBUAE, or other competent authorities when required by law.</Li>
            <Li><strong>Business transfers:</strong> In the event of a merger, acquisition, or asset sale, personal data may be transferred with appropriate safeguards.</Li>
          </Section>

          <Section title="5. Data Retention">
            <P>
              We retain personal data for as long as your account is active or as needed to provide services. Compliance-related records (KYC files, screening logs, audit trails) are retained for a minimum of five (5) years from the date of the last transaction in accordance with UAE AML/CFT legislation. You may request deletion of marketing data at any time.
            </P>
          </Section>

          <Section title="6. Data Security">
            <P>
              We implement industry-standard technical and organisational measures including AES-256 encryption at rest, TLS 1.2+ encryption in transit, role-based access controls, multi-factor authentication, and regular penetration testing. Our infrastructure is hosted in ISO 27001-certified data centres. No method of transmission or storage is 100% secure; we continuously monitor and improve our security posture.
            </P>
          </Section>

          <Section title="7. International Transfers">
            <P>
              Your data is primarily processed and stored within the UAE. Where data is transferred to service providers outside the UAE, we ensure adequate protections are in place through Standard Contractual Clauses or equivalent mechanisms recognised under the UAE PDPL.
            </P>
          </Section>

          <Section title="8. Your Rights">
            <P>Subject to applicable law, you have the right to:</P>
            <Li>Access a copy of the personal data we hold about you.</Li>
            <Li>Correct inaccurate or incomplete data.</Li>
            <Li>Request deletion of your personal data (subject to legal retention obligations).</Li>
            <Li>Object to or restrict certain processing activities.</Li>
            <Li>Data portability — receive your data in a structured, machine-readable format.</Li>
            <Li>Withdraw consent at any time where processing is based on consent.</Li>
            <P>To exercise any of these rights, please email <Link component="button" onClick={() => setModalOpen(true)} sx={{ color: GOLD, cursor: "pointer", fontFamily: "IBM Plex Sans, sans-serif", fontSize: "inherit", background: "none", border: "none", padding: 0, verticalAlign: "baseline" }}>Contact us</Link>. We will respond within 30 days.</P>
          </Section>

          <Section title="9. Cookies">
            <P>
              We use cookies and similar tracking technologies to operate and improve the Platform. Please see our <Link href="/cookie-policy" sx={{ color: GOLD }}>Cookie Policy</Link> for full details and opt-out options.
            </P>
          </Section>

          <Section title="10. Children's Privacy">
            <P>
              The Platform is intended for business users aged 18 and over. We do not knowingly collect personal data from minors. If you believe a minor has provided us data, please contact us immediately.
            </P>
          </Section>

          <Section title="11. Changes to This Policy">
            <P>
              We may update this Privacy Policy periodically. We will notify registered users by email and post the updated policy on this page with a revised "Last updated" date. Continued use of the Platform after changes constitutes acceptance.
            </P>
          </Section>

          <Section title="12. Contact Us">
            <P>
              For privacy-related enquiries, contact our Data Protection Officer at:
            </P>
            <Box sx={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "12px", p: 3 }}>
              <Typography sx={{ fontSize: 14, color: "#475569", lineHeight: 2, fontFamily: "IBM Plex Sans, sans-serif" }}>
                <strong style={{ color: "#0F172A" }}>AMLDiligence / ZAHADS Chartered Accountants LLC</strong><br />
                Office 751, Tamani Art Offices, Marasi Drive<br />
                Business Bay, Dubai, UAE<br />
                Email: <Link component="button" onClick={() => setModalOpen(true)} sx={{ color: GOLD, cursor: "pointer", fontFamily: "IBM Plex Sans, sans-serif", fontSize: "inherit", background: "none", border: "none", padding: 0, verticalAlign: "baseline" }}>Contact us</Link><br />
                Phone: <Link href="tel:+97144519930" sx={{ color: GOLD }}>+971 (4) 451 9930</Link>
              </Typography>
            </Box>
          </Section>

        </Box>
      </Container>

      {/* Footer bar */}
      <Box sx={{ background: "#0F172A", py: 3, borderTop: "1px solid rgba(255,255,255,.06)" }}>
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: 12, color: "#4B5563", fontFamily: "IBM Plex Sans, sans-serif" }}>
              © {new Date().getFullYear()} AMLDiligence.com · ZAHADS Chartered Accountants. All rights reserved.
            </Typography>
            <Box sx={{ display: "flex", gap: 3 }}>
              {[["Privacy Policy", "/privacy-policy"], ["Terms of Service", "/terms-of-service"], ["Cookie Policy", "/cookie-policy"]].map(([label, href]) => (
                <Link key={label} href={href} underline="none" sx={{ fontSize: 12, color: "#4B5563", fontFamily: "IBM Plex Sans, sans-serif", "&:hover": { color: "#9CA3AF" } }}>{label}</Link>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
